/**
 * BaaS Contas internal transfers (Root / PaySmart CPF-CNPJ → Operacional/Overdraft/Program/WALLET/CPF)
 * and virtual-shell saque requests to VALUT.
 * Via MobileService `/v1/ib/console/*` → BaaS.
 * Shells/reserves are never debit origins.
 */



import { baasProxy } from "./baasConsoleClient";



const ROLE_LABEL_KEYS = {

  root: "baas.accounts.transfer.roleRoot",

  operating: "baas.accounts.transfer.roleOperating",

  overdraft_reserve: "baas.accounts.transfer.roleOverdraft",

  program_reserve: "baas.accounts.transfer.roleProgram",

  person: "baas.accounts.transfer.rolePerson",

  segregation_wallet: "baas.accounts.transfer.roleWallet",

  wallet: "baas.accounts.transfer.roleWallet",

  otc: "baas.accounts.transfer.roleOtc",

  otc_virtual: "baas.accounts.transfer.roleOtc",

};



export const getInternalTransferRoleLabelKey = (role) =>

  ROLE_LABEL_KEYS[String(role || "").toLowerCase()] || null;



const mapBalances = (balances = []) =>

  (Array.isArray(balances) ? balances : [])

    .map((item) => {

      const currency = String(item.currency_code || item.currency || "").toUpperCase();

      if (!currency) return null;

      return {

        currency,

        available: Number(item.available ?? 0) / 100,

        blocked: Number(item.blocked ?? 0) / 100,

        future: Number(item.future ?? 0) / 100,

        availableMinor: Number(item.available ?? 0),

      };

    })

    .filter(Boolean);



const mapOption = (item) => {

  if (!item?.account_id) return null;

  const role = String(item.role || "").toLowerCase();
  // Contas virtuais / isBaasExitRailVirtualAccount need UI `type` (wallet|otc),
  // not only transfer-options `role` (segregation_wallet|otc_virtual).
  let type = "";
  if (role === "segregation_wallet" || role === "wallet") type = "wallet";
  else if (role === "otc_virtual" || role === "otc") type = "otc";
  else if (role === "root") type = "root";
  else if (role === "operating") type = "operational";
  else if (role === "overdraft_reserve") type = "overdraft";
  else if (role === "program_reserve") type = "program";

  return {

    id: String(item.account_id).trim(),

    role,

    type: type || undefined,

    name: item.name || item.role || item.account_id,

    entityId: item.entity_id ? String(item.entity_id).trim() : "",

    isVirtual: item.is_virtual === true,

    is_virtual: item.is_virtual === true,

    canTransferOut: item.can_transfer_out === true,

    canRequestValutWithdrawal: item.can_request_valut_withdrawal === true,

    balances: mapBalances(item.balances),

  };

};



/** Currency codes with an opening/balance row on the account. */

export const getAccountCurrencyCodes = (account) =>

  (account?.balances || [])

    .map((balance) => String(balance.currency || "").toUpperCase())

    .filter(Boolean);



/** Roles that may never debit via Contas transfer (shells / reserves / exit-rail). */
const RESERVE_OR_SHELL_SOURCE_ROLES = new Set([
  "operating",
  "overdraft_reserve",
  "program_reserve",
  "segregation_wallet",
  "wallet",
  "otc",
  "otc_virtual",
]);

/** Instance ENTITY shells that receive PROGRAM_RESERVE / operating funding. */
export const SHELL_FUND_DESTINATION_ROLES = [
  "operating",
  "overdraft_reserve",
  "program_reserve",
];

/**
 * Root is exit-rail / matriz pin — never an Abastecer shells destination.
 * Locked by role, isRoot flags, or instance RootAccountId match.
 */
export const isShellFundRootLocked = (account, { rootAccountId } = {}) => {
  if (!account) return true;
  const role = String(account.role || "").toLowerCase();
  if (role === "root") return true;
  if (account.isRoot === true || account.is_root === true) return true;
  const rootId = String(rootAccountId || "").trim();
  if (rootId && rootId === String(account.id || "").trim()) return true;
  return false;
};

/** True only for OPERATING / OVERDRAFT / PROGRAM — Root is always locked out. */
export const isShellFundDestination = (account, opts = {}) => {
  if (isShellFundRootLocked(account, opts)) return false;
  return SHELL_FUND_DESTINATION_ROLES.includes(String(account?.role || "").toLowerCase());
};

/**
 * Internal transfers debit Root or real PaySmart peers (can_transfer_out).
 * Reserves / shells / exit-rail wallets are credit-only — never outbound sources.
 */
export const isEligibleInternalTransferSource = (account) => {
  if (!account || account.canTransferOut !== true) return false;
  if (account.isVirtual === true) return false;
  const role = String(account.role || "").toLowerCase();
  if (RESERVE_OR_SHELL_SOURCE_ROLES.has(role)) return false;
  return true;
};

/**
 * Abastecer shells origin: instance Root only (VALUT-GLOBAL / partner RootAccountId).
 * OPERATING / OVERDRAFT / PROGRAM and CPF/CNPJ peers cannot debit this flow —
 * inbound issuer settlement credits Root; Root then supplies the shells.
 */
export const isEligibleShellFundSource = (account, opts = {}) => {
  if (!isEligibleInternalTransferSource(account)) return false;
  return isShellFundRootLocked(account, opts);
};

/** True when at least one eligible debit source exists (ignore reserve balances). */
export const canInitiateInternalTransfer = (sourceAccounts = []) =>
  (Array.isArray(sourceAccounts) ? sourceAccounts : []).some(isEligibleInternalTransferSource);



/** Same-currency only — no FX on internal transfer. */

export const assertSameTransferCurrency = (sourceCurrency, destinationCurrency) => {

  const source = String(sourceCurrency || "").trim().toUpperCase();

  const destination = String(destinationCurrency || "").trim().toUpperCase();

  if (!source || !destination) {

    const error = new Error("currency_required");

    error.code = "currency_required";

    throw error;

  }

  if (source !== destination) {

    const error = new Error("currency_mismatch");

    error.code = "currency_mismatch";

    throw error;

  }

  return source;

};



export const fetchInternalTransferOptions = async (instance) => {

  if (!instance) {

    return { sourceAccounts: [], destinationAccounts: [], virtualAccounts: [] };

  }



  const data = await baasProxy({

    instance,

    method: "GET",

    baasPath: "v1/accounts/internal-transfer-options",

  });



  return {

    sourceAccounts: (data?.source_accounts || []).map(mapOption).filter(Boolean),

    destinationAccounts: (data?.destination_accounts || []).map(mapOption).filter(Boolean),

    virtualAccounts: (data?.virtual_accounts || []).map(mapOption).filter(Boolean),

  };

};



/** Major units → minor (cents). */

export const toMinorUnits = (amountMajor) => {

  const value = Number(amountMajor);

  if (!Number.isFinite(value) || value <= 0) return null;

  return Math.round(value * 100);

};



export const createInternalTransfer = async (

  instance,

  {

    fromAccountId,

    toAccountId,

    sourceCurrencyCode,

    destinationCurrencyCode,

    currencyCode,

    amountMajor,

    reference,

  },

) => {

  const amount = toMinorUnits(amountMajor);

  if (!amount) {

    const error = new Error("invalid_amount");

    error.code = "invalid_amount";

    throw error;

  }



  const source = String(sourceCurrencyCode || currencyCode || "").toUpperCase();

  const destination = String(destinationCurrencyCode || currencyCode || "").toUpperCase();

  const resolved = assertSameTransferCurrency(source, destination);



  return baasProxy({

    instance,

    method: "POST",

    baasPath: `v1/accounts/${encodeURIComponent(fromAccountId)}/internal-transfers`,

    body: {

      to_account_id: toAccountId,

      source_currency_code: resolved,

      destination_currency_code: resolved,

      currency_code: resolved,

      amount,

      ...(reference ? { reference } : {}),

    },

  });

};



export const createValutWithdrawalRequest = async (

  instance,

  { fromAccountId, currencyCode, amountMajor, reference },

) => {

  const amount = toMinorUnits(amountMajor);

  if (!amount) {

    const error = new Error("invalid_amount");

    error.code = "invalid_amount";

    throw error;

  }



  return baasProxy({

    instance,

    method: "POST",

    baasPath: `v1/accounts/${encodeURIComponent(fromAccountId)}/valut-withdrawals`,

    body: {

      currency_code: String(currencyCode || "").toUpperCase(),

      amount,

      ...(reference ? { reference } : {}),

    },

  });

};



export const getTransferErrorMessage = (error, intlGet) => {

  const code = error?.code || error?.data?.error;

  const detail = error?.detail || error?.data?.detail || error?.message;

  const knownKeys = {

    virtual_account_withdrawal_forbidden: "baas.accounts.transfer.errorVirtualSource",

    reserve_debit_forbidden: "baas.accounts.transfer.errorReserveDebit",

    transfer_source_not_root: "baas.accounts.transfer.errorSourceNotRoot",

    transfer_destination_not_allowed: "baas.accounts.transfer.errorDestinationNotAllowed",

    shell_fund_root_forbidden: "baas.accounts.transfer.errorShellFundRoot",

    shell_fund_root_source_forbidden: "baas.accounts.transfer.errorShellFundRootSource",

    shell_fund_source_must_be_root: "baas.accounts.transfer.errorShellFundRootSource",

    transfer_same_account: "baas.accounts.transfer.sameAccount",

    insufficient_balance: "baas.accounts.transfer.errorInsufficientBalance",

    balance_not_found: "baas.accounts.transfer.errorBalanceNotFound",

    currency_mismatch: "baas.accounts.transfer.errorCurrencyMismatch",

    currency_required: "baas.accounts.transfer.selectCurrency",

    valut_withdrawal_source_invalid: "baas.accounts.transfer.errorValutSourceInvalid",

    instance_root_missing: "baas.accounts.transfer.errorRootMissing",

    instance_root_invalid: "baas.accounts.transfer.errorRootInvalid",

    accounts_instance_mismatch: "baas.accounts.transfer.errorInstanceMismatch",

  };



  if (code && knownKeys[code] && intlGet) {

    const localized = intlGet(knownKeys[code]);

    if (localized && localized !== knownKeys[code]) return localized;

  }



  return detail || (intlGet ? intlGet("baas.accounts.transfer.errorGeneric") : "Transfer failed.");

};


